import {
  cleanUpDataChanges,
  RoutineType,
  SAVE_ROUTINE_DRAFT,
  useCreateMutation,
  useCurrentAccount,
  useCurrentLocation,
  useCustomMutation,
} from '@gripp/shared-logic';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { Button } from 'react-native-paper';
import { Colors } from '../../../themes/colors';
import CustomAlert from '../../customAlert';
import { FormContext } from '../../forms';
import { KeyboardAwareScrollView } from '../../keyboard/keyboardAwareScrollView';
import { RoutineExecutionForm } from '../types';
import { CommonSubmitButton } from './commonSubmitButton';
import { RoutineFormProps } from './routineForm';

// GraphQL mutation will be implemented when backend is ready

export const RoutineFormOld = (props: RoutineFormProps) => {
  const { t } = useTranslation();
  const [isProcessing, toggleProcessing] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [isFromDraft, setIsFromDraft] = useState(false);
  const { mutateAsync, isPending } = useCreateMutation({
    query: CREATE_ASSET_ROUTINE,
    modelName: 'assetRoutine',
  });
  const { mutateAsync: updateAsync, isPending: isUpdating } = useCreateMutation(
    {
      query: UPDATE_ASSET_ROUTINE,
      modelName: 'assetRoutine',
    }
  );
  const { mutateAsync: saveDraftAsync } = useCustomMutation({
    query: SAVE_ROUTINE_DRAFT,
  });
  const { getLocation } = useCurrentLocation();
  const { account } = useCurrentAccount();

  const ExecutionForm: RoutineExecutionForm<any, any> | undefined = useMemo(
    () => props.module.executionForm,
    [props.module.executionForm]
  );
  const formOptions = useMemo(() => {
    const options: any = {
      mode: 'onChange',
    };
    const formResolver =
      props.module.getExecutionFormResolver &&
      props.module.getExecutionFormResolver(props.routine?.config, props.state);

    // Use draft data if available, otherwise use default values
    let defaultFormData;
    if (props.draftData && !props.isIssueEditRoutine) {
      defaultFormData = props.draftData;
    } else {
      defaultFormData =
        props.module.getExecutionFormDefaultValues &&
        props.module.getExecutionFormDefaultValues(
          props.routine?.config,
          props.state
        );
    }

    if (formResolver) {
      options.resolver = formResolver;
    }
    if (defaultFormData) {
      options.defaultValues = defaultFormData;
    }

    return options;
  }, [
    props.module,
    props.routine?.config,
    props.state,
    props.draftData,
    props.isIssueEditRoutine,
  ]);

  const formMethods = useForm(formOptions);

  // Reset form when draft data is loaded
  useEffect(() => {
    if (props.draftData && !props.isIssueEditRoutine) {
      setIsFromDraft(true);
      // Use setTimeout to ensure form is fully mounted before resetting
      setTimeout(() => {
        formMethods.reset(props.draftData, {
          keepDefaultValues: false,
        });

        // Manually set dirty state on all fields
        Object.keys(props.draftData).forEach((key) => {
          formMethods.setValue(key, props.draftData[key], {
            shouldDirty: true,
            shouldValidate: false,
          });
        });
      }, 100);
    }
  }, [props.draftData, formMethods, props.isIssueEditRoutine]);

  const onSaveDraft = async (data: any) => {
    if (isProcessing) return;

    toggleProcessing(true);
    try {
      const result = await saveDraftAsync({
        input: {
          workspaceAssetId: props.workspaceAsset.id,
          routineId: props.routine?.id,
          data: data,
        },
      });

      if (result?.routine?.saveRoutineDraft?.success) {
        // Call the parent's onSaveDraft callback if provided (don't await - let it run async)
        if (props.onSaveDraft) {
          props.onSaveDraft(); // Call without await so it doesn't block
        }
        // Close the form after saving draft
        props.onCancel && props.onCancel();
      } else {
        console.error(
          'Failed to save draft:',
          result?.routine?.saveRoutineDraft?.message
        );
      }
    } catch (error) {
      console.error('Error saving draft:', error);
    } finally {
      toggleProcessing(false);
    }
  };

  const onSubmit = async (data: any) => {
    if (isProcessing) return;

    toggleProcessing(true);
    if (props?.isIssueEditRoutine) {
      const cleanUpData = cleanUpDataChanges(
        props.assetRoutine?.data,
        data,
        props.routine?.type || RoutineType.Workflow,
        account?.id || ''
      );

      const input: any = {
        id: props.assetRoutine?.id,
        data: {
          ...cleanUpData,
        },
        updatedAt: new Date(),
      };

      if (props.sourceIssue) input.sourceIssue = { id: props.sourceIssue.id };

      await updateAsync(
        { input },
        {
          onSettled: async () => {
            await props.onCompleted('issue-edit');
            toggleProcessing(false);
          },
        }
      );
      return;
    }

    const input: any = {
      asset: { id: props.workspaceAsset.asset.id },
      workspaceAsset: { id: props.workspaceAsset.id },
      workspace: { id: props.workspaceAsset.workspace.id },
      routine: { id: props.routine?.id },
      routineVersion: { id: props.routine?.currentVersion?.id },
      data: data,
      location: await getLocation(),
      createdFrom: props.createdFrom,
    };
    if (props.sourceIssue) input.sourceIssue = { id: props.sourceIssue.id };

    await mutateAsync(
      { input },
      {
        onSettled: async () => {
          await props.onCompleted();
          toggleProcessing(false);
        },
      }
    );
  };

  return (
    <>
      <FormContext.Provider value={formMethods}>
        <props.topBar
          title={props.routine?.config?.routineButtonText ?? ''}
          onCancel={() => {
            setConfirmCancel(false);
            props.onCancel && props.onCancel();
          }}
          showSave={false}
          confirmBack={formMethods.formState.isDirty}
          showAlert={() => setConfirmCancel(true)}
          backButtonLabel={t('topNav.back')}
          right={props.rightButton}
          showConfirmBack={
            props.showConfirmBack && formMethods.formState.isDirty
          }
        />
        {ExecutionForm && (
          <KeyboardAwareScrollView
            containerStyle={styles.container}
            showsVerticalScrollIndicator={false}
            style={styles.scrollviewContainer}
          >
            <ExecutionForm
              config={props.routine?.config}
              currentState={props.state}
              workspaceAsset={props.workspaceAsset}
              toggleProcessing={toggleProcessing}
              isIssueEditRoutine={props.isIssueEditRoutine}
            />
            {!props.preview && (
              <View style={styles.row}>
                <CommonSubmitButton
                  formState={formMethods.formState}
                  onPress={formMethods.handleSubmit(onSubmit)}
                  buttonText={
                    props?.isIssueEditRoutine
                      ? t('routines.resubmit')
                      : t('routines.submit')
                  }
                  isPending={isProcessing || isPending || isUpdating}
                  isFromDraft={isFromDraft}
                />
                {props.showSaveDraftButton && !props.isIssueEditRoutine && (
                  <Button
                    mode="outlined"
                    style={styles.saveDraftButton}
                    labelStyle={[
                      styles.saveDraftButtonLabel,
                      (!formMethods.formState.isDirty || isProcessing) &&
                        styles.saveDraftDisabledButtonLabel,
                    ]}
                    onPress={() => {
                      const formData = formMethods.getValues();
                      onSaveDraft(formData);
                    }}
                    disabled={!formMethods.formState.isDirty || isProcessing}
                  >
                    {t('routines.saveDraft')}
                  </Button>
                )}
              </View>
            )}
          </KeyboardAwareScrollView>
        )}
        {props.footer && props.footer}
      </FormContext.Provider>
      <CustomAlert
        visible={confirmCancel}
        primaryButtonLabel={t('routines.modalCancel.primary')}
        secondaryButtonLabel={t('routines.modalCancel.secondary')}
        tertiaryButtonLabel={
          props.showSaveDraftButton
            ? t('topNav.customAlert.saveProgress')
            : undefined
        }
        action={() => {
          props.onCancel && props.onCancel();
        }}
        tertiaryAction={async () => {
          const formData = formMethods.getValues();
          await onSaveDraft(formData);
        }}
        hideAlert={() => setConfirmCancel(false)}
        showNativeModal={props.showNativeModal}
      />
    </>
  );
};

const CREATE_ASSET_ROUTINE = `
  mutation CreateAssetRoutine($input: CreateAssetRoutineInput!) {
    createAssetRoutine(input: $input) {
      id
    }
  }
`;

const UPDATE_ASSET_ROUTINE = `
  mutation UpdateAssetRoutine($input: MutateAssetRoutineInput!) {
    updateAssetRoutine(input: $input) {
      id
      data
      updatedAt
    }
  }
`;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'column',
    padding: 20,
    backgroundColor: Colors.primaryGrayBackground,
  },
  row: {
    flexDirection: 'column',
    justifyContent: 'center',
    marginTop: 20,
    marginBottom: 10,
  },
  scrollviewContainer: {
    flex: 1,
    marginBottom: 20,
  },
  saveDraftButton: {
    marginTop: 12,
    borderWidth: 0,
    borderRadius: 12,
    height: 52,
    justifyContent: 'center',
  },
  saveDraftButtonLabel: {
    fontSize: 16,
    fontWeight: '600',
    textAlignVertical: 'center',
    color: Colors.black,
  },
  saveDraftDisabledButtonLabel: {
    color: Colors.grayDisabled,
  },
});
